import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const FilterPanel = ({ filters, onFilterChange, resultCount, onClearFilters }) => {
  const [isExpanded, setIsExpanded] = useState({
    specifications: false,
    features: false
  });

  const trailerTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'utility', label: 'Utility Trailer' },
    { value: 'enclosed', label: 'Enclosed Trailer' },
    { value: 'flatbed', label: 'Flatbed Trailer' },
    { value: 'car-hauler', label: 'Car Hauler' },
    { value: 'dump', label: 'Dump Trailer' },
    { value: 'equipment', label: 'Equipment Trailer' }
  ];

  const hitchTypeOptions = [
    { value: '', label: 'All Hitch Types' },
    { value: 'ball', label: 'Ball Hitch' },
    { value: 'pintle', label: 'Pintle Hitch' },
    { value: 'gooseneck', label: 'Gooseneck' },
    { value: 'fifth-wheel', label: 'Fifth Wheel' }
  ];

  const handleToggleSection = (section) => {
    setIsExpanded(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground">
            Filter Results
          </h2>
          <p className="font-caption text-sm text-muted-foreground mt-1">
            {resultCount} trailers found
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          iconName="X"
          iconPosition="left"
        >
          Clear All
        </Button>
      </div>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input
            type="text"
            label="Location"
            placeholder="City or zip code"
            value={filters?.location}
            onChange={(e) => onFilterChange('location', e?.target?.value)}
          />

          <Input
            type="date"
            label="Pickup Date"
            value={filters?.pickupDate}
            onChange={(e) => onFilterChange('pickupDate', e?.target?.value)}
            min={new Date()?.toISOString()?.split('T')?.[0]}
          />

          <Input
            type="date"
            label="Return Date"
            value={filters?.returnDate}
            onChange={(e) => onFilterChange('returnDate', e?.target?.value)}
            min={filters?.pickupDate || new Date()?.toISOString()?.split('T')?.[0]}
          />

          <Select
            label="Trailer Type"
            options={trailerTypeOptions}
            value={filters?.trailerType}
            onChange={(value) => onFilterChange('trailerType', value)}
            id="trailer-type"
            name="trailerType"
            description=""
            error=""
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="font-body text-sm font-medium text-foreground mb-2 block">
              Price Range (per day)
            </label>
            <div className="flex items-center space-x-3">
              <Input
                type="number"
                placeholder="Min"
                value={filters?.minPrice}
                onChange={(e) => onFilterChange('minPrice', e?.target?.value)}
                min="0"
              />
              <span className="text-muted-foreground">-</span>
              <Input
                type="number"
                placeholder="Max"
                value={filters?.maxPrice}
                onChange={(e) => onFilterChange('maxPrice', e?.target?.value)}
                min="0"
              />
            </div>
          </div>

          <Input
            type="number"
            label="Distance Radius (miles)"
            placeholder="e.g., 25"
            value={filters?.radius}
            onChange={(e) => onFilterChange('radius', e?.target?.value)}
            min="1"
          />
        </div>

        <div className="border-t border-border pt-6">
          <button
            onClick={() => handleToggleSection('specifications')}
            className="w-full flex items-center justify-between p-3 bg-muted rounded-md hover:bg-muted/80 transition-smooth"
          >
            <span className="font-body font-medium text-foreground">
              Specifications
            </span>
            <Icon
              name={isExpanded?.specifications ? 'ChevronUp' : 'ChevronDown'}
              size={20}
              strokeWidth={2}
            />
          </button>

          {isExpanded?.specifications && (
            <div className="mt-4 space-y-4 pl-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  type="number"
                  label="Min Payload Capacity (lbs)"
                  placeholder="e.g., 2000"
                  value={filters?.minPayload}
                  onChange={(e) => onFilterChange('minPayload', e?.target?.value)}
                  min="0"
                />

                <Select
                  label="Hitch Type"
                  options={hitchTypeOptions}
                  value={filters?.hitchType}
                  onChange={(value) => onFilterChange('hitchType', value)}
                  id="hitch-type"
                  name="hitchType"
                  description=""
                  error=""
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  type="number"
                  label="Min Length (ft)"
                  placeholder="e.g., 10"
                  value={filters?.minLength}
                  onChange={(e) => onFilterChange('minLength', e?.target?.value)}
                  min="0"
                />

                <Input
                  type="number"
                  label="Min Width (ft)"
                  placeholder="e.g., 6"
                  value={filters?.minWidth}
                  onChange={(e) => onFilterChange('minWidth', e?.target?.value)}
                  min="0"
                />
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-border pt-6">
          <button
            onClick={() => handleToggleSection('features')}
            className="w-full flex items-center justify-between p-3 bg-muted rounded-md hover:bg-muted/80 transition-smooth"
          >
            <span className="font-body font-medium text-foreground">
              Special Features
            </span>
            <Icon
              name={isExpanded?.features ? 'ChevronUp' : 'ChevronDown'}
              size={20}
              strokeWidth={2}
            />
          </button>

          {isExpanded?.features && (
            <div className="mt-4 space-y-3 pl-3">
              <Checkbox
                label="Electric Brakes"
                checked={filters?.electricBrakes}
                onChange={(e) => onFilterChange('electricBrakes', e?.target?.checked)}
              />
              <Checkbox
                label="Ramps Included"
                checked={filters?.ramps}
                onChange={(e) => onFilterChange('ramps', e?.target?.checked)}
              />
              <Checkbox
                label="Tie-Down Points"
                checked={filters?.tieDowns}
                onChange={(e) => onFilterChange('tieDowns', e?.target?.checked)}
              />
              <Checkbox
                label="Side Rails"
                checked={filters?.sideRails}
                onChange={(e) => onFilterChange('sideRails', e?.target?.checked)}
              />
              <Checkbox
                label="Spare Tire"
                checked={filters?.spareTire}
                onChange={(e) => onFilterChange('spareTire', e?.target?.checked)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;